import React, { useState, useEffect, useRef, useMemo } from "react";
import { FaSearch, FaBuilding, FaSpinner, FaMapMarkerAlt, FaSlidersH } from 'react-icons/fa';
import "./Header.css";

import { getLandUseTypes, searchProjects, getRecentProjects, getCities, getDevelopers, unifiedSearch, getProjectSuggestions } from "../../services/api";
import { useProjects } from "../../contexts/ProjectsContext";
import { getTranslation } from "../../utils/translations";
import { useLanguage } from "../../App";
import { API_CONFIG } from "../../config/constants";

// Format price in short human-friendly form (500K, 1.2M, 2 مليون, ...)
const formatShortPrice = (value, language) => {
  if (value >= 1_000_000) {
    const m = value / 1_000_000;
    const rounded = m % 1 === 0 ? m.toFixed(0) : m.toFixed(1);
    return language === 'ar' ? `${rounded} مليون` : `${rounded}M`;
  }
  if (value >= 1000) {
    const k = value / 1000;
    const rounded = k % 1 === 0 ? k.toFixed(0) : k.toFixed(1);
    return language === 'ar' ? `${rounded} ألف` : `${rounded}K`;
  }
  return new Intl.NumberFormat(language === 'ar' ? 'ar-SA' : 'en-US').format(value);
};

// Build dynamic price chips from actual data range
const buildPriceChips = (min, max, language) => {
  if (!isFinite(min) || !isFinite(max) || max <= min) return [];

  const span = max - min;
  const rawStep = span / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const niceSteps = [1, 2, 2.5, 5, 10];
  const step =
    (niceSteps.find(s => s * magnitude >= rawStep) || 10) * magnitude;

  const breakpoints = [];
  let current = Math.ceil(min / step) * step;
  while (current < max && breakpoints.length < 4) {
    if (current > min) breakpoints.push(current);
    current += step;
  }

  if (breakpoints.length === 0) return [];

  const chips = [];
  chips.push({
    id: `under-${breakpoints[0]}`,
    label:
      language === 'ar'
        ? `أقل من ${formatShortPrice(breakpoints[0], language)}`
        : `Under ${formatShortPrice(breakpoints[0], language)}`,
    range: { min, max: breakpoints[0] }
  });
  for (let i = 0; i < breakpoints.length - 1; i++) {
    chips.push({
      id: `range-${breakpoints[i]}-${breakpoints[i + 1]}`,
      label: `${formatShortPrice(breakpoints[i], language)} – ${formatShortPrice(breakpoints[i + 1], language)}`,
      range: { min: breakpoints[i], max: breakpoints[i + 1] }
    });
  }
  chips.push({
    id: `over-${breakpoints[breakpoints.length - 1]}`,
    label:
      language === 'ar'
        ? `أكثر من ${formatShortPrice(breakpoints[breakpoints.length - 1], language)}`
        : `Over ${formatShortPrice(breakpoints[breakpoints.length - 1], language)}`,
    range: { min: breakpoints[breakpoints.length - 1], max }
  });
  return chips;
};

const PriceChipsDropdown = ({
  language,
  priceStats,
  tempPriceFilter,
  showCustomPrice,
  setShowCustomPrice,
  customMinPrice,
  setCustomMinPrice,
  customMaxPrice,
  setCustomMaxPrice,
  onApplyChip,
  onApplyCustom,
  onClear,
}) => {
  const chips = useMemo(
    () => buildPriceChips(priceStats.min, priceStats.max, language),
    [priceStats.min, priceStats.max, language]
  );

  const activeChipId = useMemo(() => {
    const match = chips.find(
      c => c.range.min === tempPriceFilter.min && c.range.max === tempPriceFilter.max
    );
    return match?.id || null;
  }, [chips, tempPriceFilter.min, tempPriceFilter.max]);

  const isAllSelected =
    tempPriceFilter.min === priceStats.min && tempPriceFilter.max === priceStats.max;

  return (
    <div className="price-dropdown price-chips-dropdown">
      <div className="price-chips-row">
        <button
          type="button"
          className={`price-chip ${isAllSelected && !showCustomPrice ? 'active' : ''}`}
          onClick={onClear}
        >
          {language === 'ar' ? 'الكل' : 'All'}
        </button>
        {chips.map(chip => (
          <button
            key={chip.id}
            type="button"
            className={`price-chip ${activeChipId === chip.id ? 'active' : ''}`}
            onClick={() => onApplyChip(chip.range)}
          >
            {chip.label}
          </button>
        ))}
        <button
          type="button"
          className={`price-chip price-chip-custom ${showCustomPrice ? 'active' : ''}`}
          onClick={() => setShowCustomPrice(v => !v)}
        >
          <FaSlidersH className="chip-icon" />
          {language === 'ar' ? 'مخصص' : 'Custom'}
        </button>
      </div>

      {showCustomPrice && (
        <div className="price-custom-inputs">
          <div className="price-input-group">
            <label>{language === 'ar' ? 'من' : 'From'}</label>
            <input
              type="number"
              inputMode="numeric"
              placeholder={formatShortPrice(priceStats.min, language)}
              value={customMinPrice}
              onChange={(e) => setCustomMinPrice(e.target.value)}
              min={priceStats.min}
              max={priceStats.max}
            />
          </div>
          <div className="price-input-group">
            <label>{language === 'ar' ? 'إلى' : 'To'}</label>
            <input
              type="number"
              inputMode="numeric"
              placeholder={formatShortPrice(priceStats.max, language)}
              value={customMaxPrice}
              onChange={(e) => setCustomMaxPrice(e.target.value)}
              min={priceStats.min}
              max={priceStats.max}
            />
          </div>
          <button
            type="button"
            className="price-apply-btn"
            onClick={onApplyCustom}
            disabled={!customMinPrice && !customMaxPrice}
          >
            {language === 'ar' ? 'تطبيق' : 'Apply'}
          </button>
        </div>
      )}

      {!isAllSelected && !showCustomPrice && (
        <div className="price-current-range">
          <span>
            {formatShortPrice(tempPriceFilter.min, language)} – {formatShortPrice(tempPriceFilter.max, language)}
            {' '}
            {language === 'ar' ? 'ر.س' : 'SAR'}
          </span>
        </div>
      )}
    </div>
  );
};


const Header = ({
  selectedProject,
  setSelectedProject,
  onParcelSearch,
  onToggleFilters,
  onStatusFiltersChange,
  statusFilters = [],
  totalUnitsCount = 0,
  onPriceFilterChange,
  priceFilter = { min: 0, max: 1000000 },
  priceStats = { min: 0, max: 1000000 },
  onUsageFilterChange,
  usageFilters = [],
  isSharedView = false,
  // New props for dual mode support
  mode = 'unit', // 'project' or 'unit'
  onProjectSelect,
  onProjectSearch,
  onProjectFiltersChange,
  projectFilters: projectFiltersProp = { status: [], city: '' }, // Add projectFilters as prop
  // New props for unified search
  onUnifiedSearch,
  onShowSearchResults,
  onShowAllResults,
  onStartSearch,
  // New props for floors filter
  onFloorFilterChange,
  floorFilters = [],
  projectFloors = [],
  // New prop for city zoom functionality
  onCityZoom,
  // New prop for map interaction tracking
  mapInteracted = false
}) => {
  const { language } = useLanguage();
  const { projects, loading: projectsLoading, setSelectedFloor, selectedFloor } = useProjects();
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("all"); // 'all', 'projects', 'units', 'developers'
  const [isSearching, setIsSearching] = useState(false); // Loading state for search
  const [activeTab, setActiveTab] = useState(mode === 'project' ? 'project-status' : 'status'); // 'search', 'status', 'price', 'usage', 'floors', 'project-search', 'project-status', 'project-city', 'recent-projects'
  const [tempPriceFilter, setTempPriceFilter] = useState(priceFilter);
  const [showCustomPrice, setShowCustomPrice] = useState(false);
  const [customMinPrice, setCustomMinPrice] = useState('');
  const [customMaxPrice, setCustomMaxPrice] = useState('');
  const [landUseTypes, setLandUseTypes] = useState([]);
  const [isLoadingLandUseTypes, setIsLoadingLandUseTypes] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track if this is the first load
  const [hideRecentProjectsPermanently, setHideRecentProjectsPermanently] = useState(false); // Hide recent projects after map interaction

  // Google Places Auto-complete state
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);
  
  // Project suggestions state
  const [projectSuggestions, setProjectSuggestions] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const GOOGLE_PLACES_API_KEY = "AIzaSyBLZfb-0-QqxufwC0_XMi8D0ZVwf8gP4pU";

  // Project search state - Use prop from App.js instead of local state
  const projectFilters = projectFiltersProp;
  const [recentProjects, setRecentProjects] = useState([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [cities, setCities] = useState([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [developers, setDevelopers] = useState([]);
  const [isLoadingDevelopers, setIsLoadingDevelopers] = useState(false);

  // Filter buttons state
  const [activeFilterTab, setActiveFilterTab] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    city: '',
    unitStatus: [],
    projectStatus: [],
    usage: [],
    unitType: [], // Sub property type filter
    priceRange: '',
    developer: '',
    preferences: '',
    deliveryDate: null,
    deliveryDateFrom: null,
    deliveryDateTo: null
  });

  useEffect(() => {
    const loadLandUseTypes = async () => {
      setIsLoadingLandUseTypes(true);
      try {
        const response = await getLandUseTypes();
        setLandUseTypes(response.data.data);
      } catch (error) {
      } finally {
        setIsLoadingLandUseTypes(false);
      }
    };

    loadLandUseTypes();
  }, []);

  useEffect(() => {
    const loadDevelopers = async () => {
      setIsLoadingDevelopers(true);
      try {
        const response = await getDevelopers();
        setDevelopers(response.data.data);
      } catch (error) {
      } finally {
        setIsLoadingDevelopers(false);
      }
    };

    loadDevelopers();
  }, []);

  // Load recent projects and cities when project mode is active
  useEffect(() => {
    if (mode === 'project') {
      const loadRecentProjects = async () => {
        setIsLoadingRecent(true);
        try {
          const response = await getRecentProjects(2);
          if (response.data.success) {
            setRecentProjects(response.data.data);
          }
        } catch (error) {
        } finally {
          setIsLoadingRecent(false);
        }
      };

      const loadCities = async () => {
        setIsLoadingCities(true);
        try {
          const response = await getCities();
          if (response.data.success) {
            setCities(response.data.data);
          }
        } catch (error) {
          setCities([]);
        } finally {
          setIsLoadingCities(false);
        }
      };

      loadRecentProjects();
      loadCities();
    }
  }, [mode]);

  // Hide recent projects immediately on any map interaction (click, pan, zoom)
  // They won't reappear during the session; a fresh page load shows them again.
  useEffect(() => {
    if (mapInteracted && !hideRecentProjectsPermanently) {
      setHideRecentProjectsPermanently(true);
    }
  }, [mapInteracted, hideRecentProjectsPermanently]);

  useEffect(() => {
    const handleClickOutside = (event) => {
   
      // Close filter dropdowns
      if (activeFilterTab && !event.target.closest('.filter-buttons-container') && !event.target.closest('.filter-dropdown')) {
        setActiveFilterTab(null);
      }
      
      // Close suggestions dropdown
      if (showLocationSuggestions && 
          !event.target.closest('.modern-search-input-container') && 
          !event.target.closest('.location-suggestions')) {
        setShowLocationSuggestions(false);
        setLocationSuggestions([]);
        setProjectSuggestions([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeFilterTab, showLocationSuggestions]);

  // Auto-activate floors tab when a residential project is selected
  useEffect(() => {
    if (selectedProject && mode === 'unit') {
      const project = projects.find(p => p.id === selectedProject);
      if (project?.project_type === 'residential' && projectFloors.length > 0) {
        // Auto-activate floors tab for residential projects
        setActiveTab('floors');
      }
    }
  }, [selectedProject, projects, mode, projectFloors.length]);

  // تحديث tempPriceFilter عند تغيير priceFilter
  useEffect(() => {
    // Only update if the values actually changed
    if (tempPriceFilter.min !== priceFilter.min || tempPriceFilter.max !== priceFilter.max) {
      setTempPriceFilter(priceFilter);
    }
  }, [priceFilter.min, priceFilter.max, tempPriceFilter.min, tempPriceFilter.max]);

  // Combined loading state for all required APIs
  const isInitialLoading = projectsLoading || isLoadingLandUseTypes || (mode === 'project' && (isLoadingCities));

  const selectedProjectData = projects.find(p => p.id === selectedProject);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    // Trigger both location and project suggestions for project mode
    if (mode === 'project' && value.length > 2) {
      fetchLocationSuggestions(value);
      fetchProjectSuggestions(value);
      setShowLocationSuggestions(true);
    } else {
      setShowLocationSuggestions(false);
      setLocationSuggestions([]);
      setProjectSuggestions([]);
    }
  };

  // Google Places API function
  const fetchLocationSuggestions = async (input) => {
    if (!input || input.length < 3) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    setIsLoadingPlaces(true);
    try {
      const response = await fetch(
        `${API_CONFIG.FULL_URL || 'http://localhost:8001/api'}/google-places/suggestions?input=${encodeURIComponent(input)}&language=${language === 'ar' ? 'ar' : 'en'}`
      );
      const data = await response.json();
      
      if (data.success && data.data) {
        setLocationSuggestions(data.data);
        setShowLocationSuggestions(true);
      } else {
        setLocationSuggestions([]);
        setShowLocationSuggestions(true);
      }
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    } finally {
      setIsLoadingPlaces(false);
    }
  };

  // Project suggestions API function
  const fetchProjectSuggestions = async (input) => {
    if (!input || input.length < 2) {
      setProjectSuggestions([]);
      return;
    }

    setIsLoadingProjects(true);
    try {
      const response = await getProjectSuggestions(input, language === 'ar' ? 'ar' : 'en', 5);
      
      if (response.data.success && response.data.data) {
        setProjectSuggestions(response.data.data);
      } else {
        setProjectSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching project suggestions:', error);
      setProjectSuggestions([]);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  // Handle location suggestion selection
  const handleLocationSelect = async (suggestion) => {
    setSearchValue(suggestion.description);
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
    
    // Get place details to get coordinates
    try {
      const response = await fetch(
        `${API_CONFIG.FULL_URL || 'http://localhost:8001/api'}/google-places/place-details?place_id=${suggestion.place_id}`
      );
      const data = await response.json();
      
      if (data.success && data.data) {
        const { lat, lng } = data.data;
        
        // Start search with location coordinates
        if (onStartSearch) {
          onStartSearch();
        }
        
        // Perform location-based search
        await performLocationBasedSearch(suggestion.description, lat, lng);
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
    }
  };

  // Handle project suggestion selection
  const handleProjectSelect = async (suggestion) => {
    setSearchValue(suggestion.description);
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
    setProjectSuggestions([]);
    
    // Start search with project name (text-based search, not location-based)
    if (onStartSearch) {
      onStartSearch();
    }
    
    // Perform text-based search for the project
    await performTextBasedSearch(suggestion.description, suggestion.name);
  };

  // Perform search based on location coordinates
  const performLocationBasedSearch = async (locationName, lat, lng) => {
    setIsSearching(true);
    try {
      // Convert selected filters to API format
      const apiFilters = convertFiltersToApiFormat(selectedFilters);

      // Add spatial parameters
      apiFilters.lat = lat;
      apiFilters.lng = lng;
      apiFilters.radius = 8;

      // Search for projects and units near the selected location with spatial parameters and filters
      const response = await unifiedSearch(locationName, searchType, 1, 10, apiFilters, 'created_at', 'desc');
      if (response.data.success && onShowSearchResults) {
        // Close autocomplete menu when search results appear
        setShowLocationSuggestions(false);
        setLocationSuggestions([]);
        setProjectSuggestions([]);
        onShowSearchResults(response.data.data, locationName, searchType);
      }
    } catch (error) {
      console.error('Error performing location-based search:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Perform text-based search (for project suggestions)
  const performTextBasedSearch = async (searchTerm, projectName) => {
    setIsSearching(true);
    try {
      // Convert selected filters to API format
      const apiFilters = convertFiltersToApiFormat(selectedFilters);

      // Search for projects and units using text search with filters
      const response = await unifiedSearch(searchTerm, searchType, 1, 10, apiFilters, 'created_at', 'desc');
      if (response.data.success && onShowSearchResults) {
        // Close autocomplete menu when search results appear
        setShowLocationSuggestions(false);
        setLocationSuggestions([]);
        setProjectSuggestions([]);
        onShowSearchResults(response.data.data, projectName, searchType);
      }
    } catch (error) {
      console.error('Error performing text-based search:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'project') {
      // Start loading immediately
      if (onStartSearch) {
        onStartSearch();
      }
      // Handle unified search for projects mode (allows search with filters only)
      await handleUnifiedSearch();
    } else if ((searchValue.trim() || hasActiveFilters()) && onParcelSearch) {
      // Handle unit search (allows search with filters only)
      onParcelSearch(searchValue.trim());
    }
  };

  const handleUnifiedSearch = async () => {
    // Allow search if there's text OR if there are active filters
    if (!searchValue.trim() && !hasActiveFilters()) return;

    setIsSearching(true);
    try {
      // Convert selected filters to API format
      const apiFilters = convertFiltersToApiFormat(selectedFilters);

      const response = await unifiedSearch(searchValue.trim() || '', searchType, 1, 10, apiFilters, 'created_at', 'desc');
      if (response.data.success && onShowSearchResults) {
        // Close autocomplete menu when search results appear
        setShowLocationSuggestions(false);
        setLocationSuggestions([]);
        setProjectSuggestions([]);
        onShowSearchResults(response.data.data, searchValue.trim() || 'Filtered Results', searchType);
      }
    } catch (error) {
    } finally {
      setIsSearching(false);
    }
  };

  const handleProjectSearch = async () => {
    if (!searchValue.trim()) return;

    try {
      const response = await searchProjects(searchValue.trim(), projectFilters);
      if (response.data.success && response.data.data.length > 0) {
        // Select the first project from search results
        const firstProject = response.data.data[0];
        if (onProjectSelect) {
          onProjectSelect(firstProject);
        }
      }
    } catch (error) {
    }
  };

  const handleProjectStatusFilter = (status) => {
    // console.log('🎛️ Header: handleProjectStatusFilter called with status:', status);
    // console.log('🎛️ Header: Current projectFilters:', projectFilters);
    let newStatus;

    if (Array.isArray(status)) {
      // If status is an array (empty array for "All"), clear all filters
      newStatus = [];
    } else {
      // Toggle individual status
      newStatus = projectFilters.status.includes(status)
        ? projectFilters.status.filter(s => s !== status)
        : [...projectFilters.status, status];
    }

    const newFilters = { ...projectFilters, status: newStatus };
    // console.log('📝 Header: New filters to send:', newFilters);

    if (onProjectFiltersChange) {
      onProjectFiltersChange(newFilters);
      // console.log('✅ Header: onProjectFiltersChange called');
    }
  };

  const handleProjectCityChange = (city) => {
    const newFilters = { ...projectFilters, city };

    if (onProjectFiltersChange) {
      onProjectFiltersChange(newFilters);
    }

    // Trigger city zoom if a specific city is selected
    if (city && onCityZoom) {
      try {
        const selectedCity = cities.find(c => c.id.toString() === city);
        if (selectedCity && selectedCity.latitude && selectedCity.longitude) {
          onCityZoom({
            latitude: parseFloat(selectedCity.latitude),
            longitude: parseFloat(selectedCity.longitude),
            name: language === 'ar' ? selectedCity.name_ar : selectedCity.name_en
          });
        } else {
          console.warn('Selected city not found or missing coordinates:', city);
        }
      } catch (error) {
        console.error('Error handling city selection:', error);
      }
    }
  };

  const handleProjectClick = (project) => {
    // Hide the recent projects bar for the rest of the session
    setHideRecentProjectsPermanently(true);

    // Use onProjectSearch to trigger the same behavior as clicking on map
    if (onProjectSearch) {
      onProjectSearch(project.id);
    }

    // Also call onProjectSelect for additional handling
    if (onProjectSelect) {
      onProjectSelect(project);
    }

    setActiveTab(null); // Close dropdown
  };

  const handleTabClick = (tab) => {
    // Mark that user has interacted with tabs (no longer initial load)
    setIsInitialLoad(false);
    
    if (activeTab === tab) {
      setActiveTab(null); // إغلاق التبويب إذا كان مفتوحاً
    } else {
      setActiveTab(tab); // فتح التبويب الجديد
    }
  };

  const handleStatusFilter = (status) => {

    if (onStatusFiltersChange) {
      const newFilters = statusFilters.includes(status)
        ? statusFilters.filter(s => s !== status)
        : [...statusFilters, status];

      onStatusFiltersChange(newFilters);
    }
  };

  const handlePriceChange = (field, value) => {
    const newFilter = { ...tempPriceFilter, [field]: parseInt(value) || 0 };
    setTempPriceFilter(newFilter);
  };

  // Apply a chip-selected range immediately
  const applyPriceChip = (range) => {
    setShowCustomPrice(false);
    setTempPriceFilter(range);
    if (onPriceFilterChange) {
      onPriceFilterChange({ ...range, isExplicitlyActivated: true });
    }
  };

  const applyPriceFilter = () => {
    // Pull from custom inputs if open, otherwise from tempPriceFilter
    let finalRange = tempPriceFilter;
    if (showCustomPrice) {
      const minNum = parseFloat(customMinPrice);
      const maxNum = parseFloat(customMaxPrice);
      const finalMin = isFinite(minNum) ? Math.max(minNum, priceStats.min) : priceStats.min;
      const finalMax = isFinite(maxNum) ? Math.min(maxNum, priceStats.max) : priceStats.max;
      if (finalMin > finalMax) return;
      finalRange = { min: finalMin, max: finalMax };
      setTempPriceFilter(finalRange);
    }
    if (onPriceFilterChange) {
      onPriceFilterChange({ ...finalRange, isExplicitlyActivated: true });
    }
  };

  const clearPriceFilter = () => {
    const defaultFilter = { min: priceStats.min, max: priceStats.max };
    setTempPriceFilter(defaultFilter);
    setShowCustomPrice(false);
    setCustomMinPrice('');
    setCustomMaxPrice('');
    if (onPriceFilterChange) {
      onPriceFilterChange({ ...defaultFilter, isExplicitlyDeactivated: true });
    }
  };

  const handleUsageFilter = (usage) => {
    if (onUsageFilterChange) {
      const newFilters = usageFilters.includes(usage)
        ? usageFilters.filter(u => u !== usage)
        : [...usageFilters, usage];
      onUsageFilterChange(newFilters);
    }
  };

  const handleFloorFilter = (floorId) => {
    // Find the floor object from projectFloors
    const floor = projectFloors.find(f => f.id === floorId);
    if (floor && setSelectedFloor) {
      // Update selectedFloor in useProjects context
      setSelectedFloor(floor);
      console.log ("setSelectedFloor",floor)
      // Also call onFloorFilterChange for backward compatibility with parent component
      if (onFloorFilterChange) {
        onFloorFilterChange([floorId]);
      }
    }
  };

  // Filter button handlers
  const handleFilterTabClick = (tab) => {
    if (activeFilterTab === tab) {
      setActiveFilterTab(null); // Close if already open
    } else {
      setActiveFilterTab(tab); // Open the selected tab
    }
  };

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    
    // Close the dropdown after selection
    setActiveFilterTab(null);
    
    // Apply the filter to the search
    applyFiltersToSearch();
  };

  const handleMultiSelectFilter = (filterType, value) => {
    setSelectedFilters(prev => {
      const currentArray = prev[filterType] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      
      return {
        ...prev,
        [filterType]: newArray
      };
    });
    
    // Apply the filter to the search
    applyFiltersToSearch();
  };

  const applyFiltersToSearch = (spatialParams = null) => {
    // This function will be called whenever filters change
    // It should trigger the search with the new filters
    if (onUnifiedSearch) {
      // Convert selected filters to API format
      const apiFilters = convertFiltersToApiFormat(selectedFilters);
      
      // Add spatial parameters if provided
      if (spatialParams) {
        apiFilters.lat = spatialParams.lat;
        apiFilters.lng = spatialParams.lng;
        apiFilters.radius = spatialParams.radius || 8;
      }
      
      // Apply filters to the unified search
      const searchParams = {
        query: searchValue.trim() || '',
        type: searchType,
        filters: apiFilters,
        page: 1,
        limit: 10
      };
      
      // Trigger search with filters (even if no text search)
      onUnifiedSearch(searchParams);
    }
  };

  // Check if any filters are selected
  const hasActiveFilters = () => {
    return selectedFilters.city ||
           selectedFilters.unitStatus.length > 0 ||
           selectedFilters.projectStatus.length > 0 ||
           selectedFilters.usage.length > 0 ||
           selectedFilters.unitType.length > 0 ||
           selectedFilters.priceRange ||
           selectedFilters.developer ||
           selectedFilters.preferences ||
           selectedFilters.deliveryDate ||
           selectedFilters.deliveryDateFrom ||
           selectedFilters.deliveryDateTo;
  };

  const convertFiltersToApiFormat = (filters) => {
    const apiFilters = {};
    
    // City filter
    if (filters.city) {
      apiFilters.city = filters.city;
    }
    
    // Unit Status filter (sales_status for units)
    if (filters.unitStatus && filters.unitStatus.length > 0) {
      apiFilters.sales_status = filters.unitStatus;
    }
    
    // Project Status filter
    if (filters.projectStatus && filters.projectStatus.length > 0) {
      apiFilters.status = filters.projectStatus;
    }
    
    // Usage filter (usage_type for units)
    if (filters.usage && filters.usage.length > 0) {
      apiFilters.usage_type = filters.usage;
    }

    // Unit Type filter (sub_property_type for units)
    if (filters.unitType && filters.unitType.length > 0) {
      apiFilters.sub_property_type = filters.unitType;
    }

    // Developer filter
    if (filters.developer) {
      apiFilters.developer = filters.developer;
    }

    // Price range filter
    if (filters.priceRange) {
      const priceRanges = {
        '0-1000000': { min_price: 0, max_price: 1000000 },
        '1000000-2000000': { min_price: 1000000, max_price: 2000000 },
        '2000000-5000000': { min_price: 2000000, max_price: 5000000 },
        '5000000+': { min_price: 5000000, max_price: null }
      };
      
      if (priceRanges[filters.priceRange]) {
        const range = priceRanges[filters.priceRange];
        if (range.min_price !== null) apiFilters.min_price = range.min_price;
        if (range.max_price !== null) apiFilters.max_price = range.max_price;
      }
    }
    
    // Preferences filter (sorting)
    if (filters.preferences) {
      if (filters.preferences === 'latest') {
        apiFilters.sort_by = 'created_at';
        apiFilters.sort_order = 'desc';
      } else if (filters.preferences === 'lowest_price') {
        apiFilters.sort_by = 'min_price';
        apiFilters.sort_order = 'asc';
      }
    }
    
    // Delivery date filter (completion_date) - Year range
    if (filters.deliveryDateFrom || filters.deliveryDateTo) {
      if (filters.deliveryDateFrom) {
        apiFilters.completion_date_from = `${filters.deliveryDateFrom}-01-01`;
      }
      if (filters.deliveryDateTo) {
        apiFilters.completion_date_to = `${filters.deliveryDateTo}-12-31`;
      }
    }

    return apiFilters;
  };

  const clearAllFilters = () => {
    setSelectedFilters({
      city: '',
      unitStatus: [],
      projectStatus: [],
      usage: [],
      unitType: [],
      priceRange: '',
      developer: '',
      preferences: '',
      deliveryDate: null,
      deliveryDateFrom: null,
      deliveryDateTo: null
    });
    setActiveFilterTab(null);

    // Re-apply search without filters
    if (onUnifiedSearch && searchValue.trim()) {
      const searchParams = {
        query: searchValue.trim(),
        type: searchType,
        filters: {},
        page: 1,
        limit: 10
      };
      onUnifiedSearch(searchParams);
    }
  };

  return (
    <header className="header">
      {/* الشريط الأساسي */}
      <div className="header-main">
        <div className="header-tabs">
          {isInitialLoading ? (
            // Show loading state while APIs are loading
           <div className="header-transition-loading">
              <div className="transition-spinner"></div>
            </div>
          ) : mode === 'project' ? (
            // Project search mode - show all options in header
            <>
              <button
                className={`header-tab search-tab ${activeTab === 'project-search' ? 'active' : ''}`}
                onClick={() => handleTabClick('project-search')}
              >
                <FaSearch className="search-tab-icon" />
                {language === "ar" ? "البحث" : "Search"}
              </button>
              {/* <button
                className="header-tab"
                onClick={() => {
                  if (onShowAllResults) {
                    onShowAllResults('all');
                  }
                }}
                style={{ background: '#10b981', color: 'white' }}
              >
                {language === "ar" ? "عرض الكل" : "Show All"}
              </button> */}
              <button
                className={`header-tab ${activeTab === 'project-status' ? 'active' : ''}`}
                onClick={() => handleTabClick('project-status')}
              >
                {language === "ar" ? "الحالة" : "Status"}
              </button>
              <button
                className={`header-tab ${activeTab === 'project-city' ? 'active' : ''}`}
                onClick={() => handleTabClick('project-city')}
              >
                {language === "ar" ? "المدينة" : "City"}
              </button>
            </>
          ) : (
            // Unit search mode (existing functionality)
            <>
              <button
                className={`header-tab search-tab ${activeTab === 'search' ? 'active' : ''}`}
                onClick={() => handleTabClick('search')}
              >
                <FaSearch className="search-tab-icon" />
                {language === "ar" ? "بحث..." : "Search..."}
              </button>
              <button
                className={`header-tab ${activeTab === 'status' ? 'active' : ''}`}
                onClick={() => handleTabClick('status')}
              >
                {language === "ar" ? "الحالة" : "Status"}
              </button>
              {/* Show floors filter only for residential projects */}
              {selectedProject && (() => {
                const project = projects.find(p => p.id === selectedProject);
                return project?.project_type === 'residential';
              })() && (
                <button
                  className={`header-tab ${activeTab === 'floors' ? 'active' : ''}`}
                  onClick={() => handleTabClick('floors')}
                >
                  {language === "ar" ? "الطوابق" : "Floors"}
                </button>
              )}
              <button
                className={`header-tab ${activeTab === 'usage' ? 'active' : ''}`}
                onClick={() => handleTabClick('usage')}
              >
                {language === "ar" ? "الإستخدام" : "Usage"}
              </button>
              <button
                className={`header-tab ${activeTab === 'price' ? 'active' : ''}`}
                onClick={() => handleTabClick('price')}
              >
                {language === "ar" ? "السعر" : "Price"}
              </button>
            </>
          )}
        </div>
      </div>

  
      {/* منطقة المحتوى المنسدل */}
      {activeTab && !isInitialLoading && (
        <div className="header-dropdown">
          {/* Project Search Dropdown */}
          {activeTab === 'project-search' && (
            <div className="project-search-dropdown">
              <div className="unified-search-container">
                <form onSubmit={handleSearchSubmit} className="modern-search-form">
                  <div className="search-type-selector">
                    <select
                      value={searchType}
                      onChange={(e) => setSearchType(e.target.value)}
                      className="search-type-select"
                    >
                      <option value="all">
                        {language === "ar" ? "الكل" : "All"}
                      </option>
                      <option value="projects">
                        {language === "ar" ? "المشاريع" : "Projects"}
                      </option>
                      <option value="units">
                        {language === "ar" ? "الوحدات" : "Units"}
                      </option>
                      <option value="developers">
                        {language === "ar" ? "المطورين" : "Developers"}
                      </option>
                    </select>
                  </div>
                  <div className="modern-search-input-container">
                    {isSearching ? (
                      <FaSpinner className="modern-search-icon spinning" />
                    ) : (
                      <FaSearch className="modern-search-icon" />
                    )}
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="modern-search-input"
                      placeholder={
                        language === "ar"
                          ? (isSearching ? "جاري البحث..." : "ابحث هنا...")
                          : (isSearching ? "Searching..." : "Search here...")
                      }
                      value={searchValue}
                      onChange={handleSearchChange}
                      disabled={isSearching}
                      autoComplete="off"
                    />
                    
                    {/* Combined Suggestions Dropdown */}
                    {showLocationSuggestions && (locationSuggestions.length > 0 || projectSuggestions.length > 0) && (
                      <div className="location-suggestions" ref={suggestionsRef}>
                        {isLoadingPlaces || isLoadingProjects ? (
                          <div className="suggestion-item loading">
                            <FaSpinner className="spinning" />
                            <span>{language === 'ar' ? 'جاري البحث...' : 'Searching...'}</span>
                          </div>
                        ) : (
                          <>
                            {/* Project Suggestions - Show first */}
                            {projectSuggestions.map((suggestion, index) => (
                              <div
                                key={`project-${suggestion.id}`}
                                className="suggestion-item project-suggestion"
                                onClick={() => handleProjectSelect(suggestion)}
                              >
                                <FaBuilding className="suggestion-icon project-icon" />
                                <div className="suggestion-content">
                                  <div className="suggestion-main-text">
                                    {suggestion.main_text}
                                  </div>
                                  {suggestion.secondary_text && (
                                    <div className="suggestion-secondary-text">
                                      {suggestion.secondary_text}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                            
                            {/* Location Suggestions - Show after projects */}
                            {locationSuggestions.map((suggestion, index) => (
                              <div
                                key={suggestion.place_id}
                                className="suggestion-item location-suggestion"
                                onClick={() => handleLocationSelect(suggestion)}
                              >
                                <FaMapMarkerAlt className="suggestion-icon location-icon" />
                                <div className="suggestion-content">
                                  <div className="suggestion-main-text">
                                    {suggestion.main_text}
                                  </div>
                                  {suggestion.secondary_text && (
                                    <div className="suggestion-secondary-text">
                                      {suggestion.secondary_text}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <button 
                    type="submit" 
                    className="modern-search-btn" 
                    disabled={isSearching || (!searchValue.trim() && !hasActiveFilters())}
                    title={(!searchValue.trim() && !hasActiveFilters()) ? 
                      (language === "ar" ? "أدخل نص للبحث أو اختر فلتر" : "Enter search text or select a filter") : 
                      (language === "ar" ? "ابحث" : "Search")
                    }
                  >
                    {isSearching ? (
                      <FaSpinner className="spinning" />
                    ) : (
                      <FaSearch />
                    )}
                  </button>
                </form>
                
                {/* Filter Buttons Container - Inside the unified search container */}
                <div className="filter-buttons-container-inline">
                  <div className="filter-buttons-scroll">
                    <button
                      className={`filter-button ${activeFilterTab === 'city' ? 'active' : ''} ${selectedFilters.city ? 'has-value' : ''}`}
                      onClick={() => handleFilterTabClick('city')}
                    >
                      {language === "ar" ? "المدينة" : "City"}
                      {selectedFilters.city && <span className="filter-indicator">●</span>}
                    </button>

                    <button
                      className={`filter-button ${activeFilterTab === 'unitType' ? 'active' : ''} ${selectedFilters.unitType.length > 0 ? 'has-value' : ''}`}
                      onClick={() => handleFilterTabClick('unitType')}
                    >
                      {language === "ar" ? "نوع الوحدة" : "Unit Type"}
                      {selectedFilters.unitType.length > 0 && <span className="filter-indicator">●</span>}
                    </button>

                    <button
                      className={`filter-button ${activeFilterTab === 'unitStatus' ? 'active' : ''} ${selectedFilters.unitStatus.length > 0 ? 'has-value' : ''}`}
                      onClick={() => handleFilterTabClick('unitStatus')}
                    >
                      {language === "ar" ? "حالة الوحدة" : "Unit Status"}
                      {selectedFilters.unitStatus.length > 0 && <span className="filter-indicator">●</span>}
                    </button>
                    
                    <button
                      className={`filter-button ${activeFilterTab === 'projectStatus' ? 'active' : ''} ${selectedFilters.projectStatus.length > 0 ? 'has-value' : ''}`}
                      onClick={() => handleFilterTabClick('projectStatus')}
                    >
                      {language === "ar" ? "حالة المشروع" : "Project Status"}
                      {selectedFilters.projectStatus.length > 0 && <span className="filter-indicator">●</span>}
                    </button>
                    
                    <button
                      className={`filter-button ${activeFilterTab === 'usage' ? 'active' : ''} ${selectedFilters.usage.length > 0 ? 'has-value' : ''}`}
                      onClick={() => handleFilterTabClick('usage')}
                    >
                      {language === "ar" ? "الاستخدام" : "Usage"}
                      {selectedFilters.usage.length > 0 && <span className="filter-indicator">●</span>}
                    </button>

                    <button
                      className={`filter-button ${activeFilterTab === 'priceRange' ? 'active' : ''} ${selectedFilters.priceRange ? 'has-value' : ''}`}
                      onClick={() => handleFilterTabClick('priceRange')}
                    >
                      {language === "ar" ? "الأسعار" : "Price"}
                      {selectedFilters.priceRange && <span className="filter-indicator">●</span>}
                    </button>

                    <button
                      className={`filter-button ${activeFilterTab === 'developer' ? 'active' : ''} ${selectedFilters.developer ? 'has-value' : ''}`}
                      onClick={() => handleFilterTabClick('developer')}
                    >
                      {language === "ar" ? "المطورين" : "Developers"}
                      {selectedFilters.developer && <span className="filter-indicator">●</span>}
                    </button>

                    <button
                      className={`filter-button ${activeFilterTab === 'preferences' ? 'active' : ''} ${selectedFilters.preferences ? 'has-value' : ''}`}
                      onClick={() => handleFilterTabClick('preferences')}
                    >
                      {language === "ar" ? "التفضيلات" : "Preferences"}
                      {selectedFilters.preferences && <span className="filter-indicator">●</span>}
                    </button>
                    
                    <button
                      className={`filter-button ${activeFilterTab === 'deliveryDate' ? 'active' : ''} ${selectedFilters.deliveryDateFrom || selectedFilters.deliveryDateTo ? 'has-value' : ''}`}
                      onClick={() => handleFilterTabClick('deliveryDate')}
                    >
                      {language === "ar" ? "تاريخ التسليم" : "Delivery Date"}
                      {(selectedFilters.deliveryDateFrom || selectedFilters.deliveryDateTo) && <span className="filter-indicator">●</span>}
                    </button>
                    
                    {/* Clear All Filters Button */}
                    {(selectedFilters.city ||
                      selectedFilters.unitStatus.length > 0 ||
                      selectedFilters.projectStatus.length > 0 ||
                      selectedFilters.usage.length > 0 ||
                      selectedFilters.unitType.length > 0 ||
                      selectedFilters.priceRange ||
                      selectedFilters.developer ||
                      selectedFilters.preferences ||
                      selectedFilters.deliveryDate ||
                      selectedFilters.deliveryDateFrom ||
                      selectedFilters.deliveryDateTo) && (
                      <button
                        className="filter-button clear-filters"
                        onClick={clearAllFilters}
                        title={language === "ar" ? "مسح جميع الفلاتر" : "Clear All Filters"}
                      >
                        {language === "ar" ? "مسح الكل" : "Clear All"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Project Status Dropdown */}
          {activeTab === 'project-status' && (
            <div className="project-status-dropdown">

              <div className="project-status-filters-grid status-title">
                <button
                  className={`project-status-filter-btn all ${projectFilters.status.length === 0 ? 'active' : ''}`}
                  onClick={() => handleProjectStatusFilter([])}
                >
                  {language === "ar" ? "الكل" : "All"}
                </button>
                {/* Dashboard-only statuses (commented out - not shown on map) */}
                {/* <button
                  className={`project-status-filter-btn pending-approval ${projectFilters.status.includes('pending_approval') ? 'active' : ''}`}
                  onClick={() => handleProjectStatusFilter('pending_approval')}
                >
                  <div className="status-indicator pending-approval"></div>
                  {language === "ar" ? "قيد الموافقة" : "Pending Approval"}
                </button> */}
                <button
                  className={`project-status-filter-btn pre-launch ${projectFilters.status.includes('pre_launch') ? 'active' : ''}`}
                  onClick={() => handleProjectStatusFilter('pre_launch')}
                >
                  <div className="status-indicator pre-launch"></div>
                  {language === "ar" ? "ما قبل البيع" : "Pre-launch"}
                </button>
                <button
                  className={`project-status-filter-btn off-plan ${projectFilters.status.includes('off_plan') ? 'active' : ''}`}
                  onClick={() => handleProjectStatusFilter('off_plan')}
                >
                  <div className="status-indicator off-plan"></div>
                  {language === "ar" ? "قيد الإنشاء" : "Off-plan"}
                </button>
                <button
                  className={`project-status-filter-btn ready ${projectFilters.status.includes('ready') ? 'active' : ''}`}
                  onClick={() => handleProjectStatusFilter('ready')}
                >
                  <div className="status-indicator ready"></div>
                  {language === "ar" ? "جاهز للتسليم" : "Ready"}
                </button>
                <button
                  className={`project-status-filter-btn unavailable ${projectFilters.status.includes('unavailable') ? 'active' : ''}`}
                  onClick={() => handleProjectStatusFilter('unavailable')}
                >
                  <div className="status-indicator unavailable"></div>
                  {language === "ar" ? "غير متاح" : "Unavailable"}
                </button>
                {/* Dashboard-only statuses (commented out - not shown on map) */}
                {/* <button
                  className={`project-status-filter-btn completed ${projectFilters.status.includes('completed') ? 'active' : ''}`}
                  onClick={() => handleProjectStatusFilter('completed')}
                >
                  <div className="status-indicator completed"></div>
                  {language === "ar" ? "مكتمل" : "Completed"}
                </button> */}
              </div>
            </div>
          )}

          {/* Project City Dropdown */}
          {activeTab === 'project-city' && (
            <div className="project-city-dropdown">
           
              <div className="project-city-filters-grid status-title">
                <button
                  className={`project-city-filter-btn all ${!projectFilters.city ? 'active' : ''}`}
                  onClick={() => handleProjectCityChange('')}
                >
                  {language === "ar" ? "الكل" : "All"}
                </button>
                {isLoadingCities ? (
                  <div className="loading-cities">
                    <div className="spinner"></div>
                    <span>{language === "ar" ? "جاري التحميل..." : "Loading..."}</span>
                  </div>
                ) : (
                  cities.map((city) => (
                    <button
                      key={city.id}
                      className={`project-city-filter-btn ${projectFilters.city == city.id ? 'active' : ''}`}
                      onClick={() => handleProjectCityChange(city.id.toString())}
                      title={language === "ar" ? `انقر للانتقال إلى ${city.name_ar}` : `Click to zoom to ${city.name_en}`}
                    >
                      {language === "ar" ? city.name_ar : city.name_en}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}



          {activeTab === 'search' && (
            <div className="search-dropdown">
              <form onSubmit={handleSearchSubmit} className="search-form">
                <input
                  type="text"
                  className="search-dropdown-input"
                  placeholder={
                    language === "ar"
                      ? "أدخل رقم الوحدة"
                      : "Enter unit number"
                  }
                  value={searchValue}
                  onChange={handleSearchChange}
                />
                <button type="submit" className="search-dropdown-btn">
                  {language === "ar" ? "ابحث" : "Search"}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'status' && (
            <div className="status-dropdown">
              {/* <div className="status-title">
                {language === "ar" ? "ألوان الخريطة" : "Map Colors"}
              </div> */}
              <div className="status-filters-grid">
                <button
                  className={`status-filter-btn all ${statusFilters.length === 0 ? 'active' : ''}`}
                  onClick={() => onStatusFiltersChange && onStatusFiltersChange([])}
                >
                  {language === "ar" ? "الكل" : "All"}
                </button>
                <button
                  className={`status-filter-btn sold ${statusFilters.includes('Sold') ? 'active' : ''}`}
                  onClick={() => handleStatusFilter('Sold')}
                >
                  <div className="status-indicator sold"></div>
                  {language === "ar" ? "مباع" : "Sold"}
                </button>
                <button
                  className={`status-filter-btn reserved ${statusFilters.includes('Reserved') ? 'active' : ''}`}
                  onClick={() => handleStatusFilter('Reserved')}
                >
                  <div className="status-indicator reserved"></div>
                  {language === "ar" ? "محجوز" : "Reserved"}
                </button>
                <button
                  className={`status-filter-btn not-available ${statusFilters.includes('Not Available') ? 'active' : ''}`}
                  onClick={() => handleStatusFilter('Not Available')}
                >
                  <div className="status-indicator not-available"></div>
                  {language === "ar" ? "غير متوفر" : "Not Available"}
                </button>
                <button
                  className={`status-filter-btn available ${statusFilters.includes('Available') ? 'active' : ''}`}
                  onClick={() => handleStatusFilter('Available')}
                >
                  <div className="status-indicator available"></div>
                  {language === "ar" ? "متاح" : "Available"}
                </button>
              </div>
              {/* <div className="status-count">
                {language === "ar" ? `${totalUnitsCount} وحدة` : `${totalUnitsCount} units`}
              </div> */}
            </div>
          )}

          {activeTab === 'usage' && (
            <div className="usage-dropdown">
              {/* <div className="usage-title">
                {language === "ar" ? "نوع استخدام الأرض" : "Land Use Type"}
              </div> */}
              <div className="usage-filters-grid">
                <button
                  className={`usage-filter-btn all ${usageFilters.length === 0 ? 'active' : ''}`}
                  onClick={() => onUsageFilterChange && onUsageFilterChange([])}
                >
                  {language === "ar" ? "الكل" : "All"}
                </button>
                {landUseTypes.map((landUseType) => (
                  <button
                    key={landUseType.id}
                    className={`usage-filter-btn ${landUseType.name_en.toLowerCase()} ${usageFilters.includes(landUseType.name_en) ? 'active' : ''}`}
                    onClick={() => handleUsageFilter(landUseType.name_en)}
                  >
                    <div className={`usage-indicator ${landUseType.name_en.toLowerCase()}`}></div>
                    {language === "ar" ? landUseType.name_ar : landUseType.name_en}
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'floors' && (
            <div className="floors-dropdown">
              {/* <div className="floors-title">
                {language === "ar" ? "الطوابق المتاحة" : "Available Floors"}
              </div> */}
              <div className="floors-filters-grid">
                {projectFloors.map((floor) => (
                  <button
                    key={floor.id}
                    className={`floors-filter-btn floor-${floor.floor_number} ${selectedFloor?.id === floor.id ? 'active' : ''}`}
                    onClick={() => handleFloorFilter(floor.id)}
                  >
                    {language === "ar" ? floor.name_ar || floor.name : floor.name_en || floor.name}
                    {floor.units_count && (
                      <span className="floor-units-count">({floor.units_count})</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'price' && (
            <PriceChipsDropdown
              language={language}
              priceStats={priceStats}
              tempPriceFilter={tempPriceFilter}
              showCustomPrice={showCustomPrice}
              setShowCustomPrice={setShowCustomPrice}
              customMinPrice={customMinPrice}
              setCustomMinPrice={setCustomMinPrice}
              customMaxPrice={customMaxPrice}
              setCustomMaxPrice={setCustomMaxPrice}
              onApplyChip={applyPriceChip}
              onApplyCustom={applyPriceFilter}
              onClear={clearPriceFilter}
            />
          )}

        </div>
      )}

      {/* Filter Dropdowns - Always available */}
      {activeFilterTab && (
        <div className="filter-dropdown">
              {/* City Filter Dropdown */}
              {activeFilterTab === 'city' && (
                <div className="filter-dropdown-content">
                  {/* <div className="filter-title">
                    {language === "ar" ? "اختر المدينة" : "Select City"}
                  </div> */}
                  <div className="filter-options">
                    <button
                      className={`filter-option ${!selectedFilters.city ? 'active' : ''}`}
                      onClick={() => handleFilterChange('city', '')}
                    >
                      {language === "ar" ? "الكل" : "All"}
                    </button>
                    {cities.map((city) => (
                      <button
                        key={city.id}
                        className={`filter-option ${selectedFilters.city === city.id.toString() ? 'active' : ''}`}
                        onClick={() => handleFilterChange('city', city.id.toString())}
                      >
                        {language === "ar" ? city.name_ar : city.name_en}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Unit Type Filter Dropdown */}
              {activeFilterTab === 'unitType' && (
                <div className="filter-dropdown-content">
                  <div className="filter-options">
                    <button
                      className={`filter-option ${selectedFilters.unitType.length === 0 ? 'active' : ''}`}
                      onClick={() => handleFilterChange('unitType', [])}
                    >
                      {language === "ar" ? "الكل" : "All"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitType.includes('residential') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitType', 'residential')}
                    >
                      {language === "ar" ? "السكني" : "Residential"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitType.includes('apartment') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitType', 'apartment')}
                    >
                      {language === "ar" ? "شقة" : "Apartment"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitType.includes('studio') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitType', 'studio')}
                    >
                      {language === "ar" ? "استوديو" : "Studio"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitType.includes('hotel_apartment') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitType', 'hotel_apartment')}
                    >
                      {language === "ar" ? "شقة فندقية" : "Hotel Apartment"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitType.includes('duplex') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitType', 'duplex')}
                    >
                      {language === "ar" ? "دوبلكس" : "Duplex"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitType.includes('loft') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitType', 'loft')}
                    >
                      {language === "ar" ? "لوفت" : "Loft"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitType.includes('penthouse') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitType', 'penthouse')}
                    >
                      {language === "ar" ? "بنتهاوس" : "Penthouse"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitType.includes('townhouse') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitType', 'townhouse')}
                    >
                      {language === "ar" ? "تاونهاوس" : "Townhouse"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitType.includes('villa') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitType', 'villa')}
                    >
                      {language === "ar" ? "فيلا" : "Villa"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitType.includes('independent_house') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitType', 'independent_house')}
                    >
                      {language === "ar" ? "منزل مستقل / بيت" : "Independent House"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitType.includes('commercial_plot') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitType', 'commercial_plot')}
                    >
                      {language === "ar" ? "أرض تجارية" : "Commercial Plot"}
                    </button>
                  </div>
                </div>
              )}

              {/* Unit Status Filter Dropdown */}
              {activeFilterTab === 'unitStatus' && (
                <div className="filter-dropdown-content">
                  {/* <div className="filter-title">
                    {language === "ar" ? "حالة الوحدة" : "Unit Status"}
                  </div> */}
                  <div className="filter-options">
                    <button
                      className={`filter-option ${selectedFilters.unitStatus.length === 0 ? 'active' : ''}`}
                      onClick={() => handleFilterChange('unitStatus', [])}
                    >
                      {language === "ar" ? "الكل" : "All"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitStatus.includes('Available') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitStatus', 'Available')}
                    >
                      {language === "ar" ? "متاح" : "Available"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitStatus.includes('Sold') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitStatus', 'Sold')}
                    >
                      {language === "ar" ? "مباع" : "Sold"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitStatus.includes('Reserved') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitStatus', 'Reserved')}
                    >
                      {language === "ar" ? "محجوز" : "Reserved"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.unitStatus.includes('Not Available') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('unitStatus', 'Not Available')}
                    >
                      {language === "ar" ? "غير متوفر" : "Not Available"}
                    </button>
                  </div>
                </div>
              )}

              {/* Project Status Filter Dropdown */}
              {activeFilterTab === 'projectStatus' && (
                <div className="filter-dropdown-content">
                  {/* <div className="filter-title">
                    {language === "ar" ? "حالة المشروع" : "Project Status"}
                  </div> */}
                  <div className="filter-options">
                    <button
                      className={`filter-option ${selectedFilters.projectStatus.length === 0 ? 'active' : ''}`}
                      onClick={() => handleFilterChange('projectStatus', [])}
                    >
                      {language === "ar" ? "الكل" : "All"}
                    </button>
                    {/* Dashboard-only statuses (commented out - not shown on map) */}
                    {/* <button
                      className={`filter-option ${selectedFilters.projectStatus.includes('pending_approval') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('projectStatus', 'pending_approval')}
                    >
                      {language === "ar" ? "قيد الموافقة" : "Pending Approval"}
                    </button> */}
                    <button
                      className={`filter-option ${selectedFilters.projectStatus.includes('pre_launch') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('projectStatus', 'pre_launch')}
                    >
                      {language === "ar" ? "ما قبل البيع" : "Pre-launch"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.projectStatus.includes('off_plan') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('projectStatus', 'off_plan')}
                    >
                      {language === "ar" ? "قيد الإنشاء" : "Off-plan"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.projectStatus.includes('ready') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('projectStatus', 'ready')}
                    >
                      {language === "ar" ? "جاهز للتسليم" : "Ready"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.projectStatus.includes('unavailable') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('projectStatus', 'unavailable')}
                    >
                      {language === "ar" ? "غير متاح" : "Unavailable"}
                    </button>
                    {/* Dashboard-only statuses (commented out - not shown on map) */}
                    {/* <button
                      className={`filter-option ${selectedFilters.projectStatus.includes('completed') ? 'active' : ''}`}
                      onClick={() => handleMultiSelectFilter('projectStatus', 'completed')}
                    >
                      {language === "ar" ? "مكتمل" : "Completed"}
                    </button> */}
                  </div>
                </div>
              )}

              {/* Usage Filter Dropdown */}
              {activeFilterTab === 'usage' && (
                <div className="filter-dropdown-content">
                  {/* <div className="filter-title">
                    {language === "ar" ? "نوع الاستخدام" : "Usage Type"}
                  </div> */}
                  <div className="filter-options">
                    <button
                      className={`filter-option ${selectedFilters.usage.length === 0 ? 'active' : ''}`}
                      onClick={() => handleFilterChange('usage', [])}
                    >
                      {language === "ar" ? "الكل" : "All"}
                    </button>
                    {landUseTypes.map((landUseType) => (
                      <button
                        key={landUseType.id}
                        className={`filter-option ${selectedFilters.usage.includes(landUseType.name_en) ? 'active' : ''}`}
                        onClick={() => handleMultiSelectFilter('usage', landUseType.name_en)}
                      >
                        {language === "ar" ? landUseType.name_ar : landUseType.name_en}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Price Range Filter Dropdown */}
              {activeFilterTab === 'priceRange' && (
                <div className="filter-dropdown-content">
                  {/* <div className="filter-title">
                    {language === "ar" ? "نطاق السعر" : "Price Range"}
                  </div> */}
                  <div className="filter-options">
                    <button
                      className={`filter-option ${!selectedFilters.priceRange ? 'active' : ''}`}
                      onClick={() => handleFilterChange('priceRange', '')}
                    >
                      {language === "ar" ? "الكل" : "All"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.priceRange === '0-1000000' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('priceRange', '0-1000000')}
                    >
                      {language === "ar" ? "1 - مليون ريال" : "1 - 1M SAR"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.priceRange === '1000000-2000000' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('priceRange', '1000000-2000000')}
                    >
                      {language === "ar" ? "مليون - 2 مليون" : "1M - 2M SAR"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.priceRange === '2000000-5000000' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('priceRange', '2000000-5000000')}
                    >
                      {language === "ar" ? "2 مليون - 5 مليون" : "2M - 5M SAR"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.priceRange === '5000000+' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('priceRange', '5000000+')}
                    >
                      {language === "ar" ? "أكثر من 5 مليون" : "5M+ SAR"}
                    </button>
                  </div>
                </div>
              )}

              {/* Developers Filter Dropdown */}
              {activeFilterTab === 'developer' && (
                <div className="filter-dropdown-content">
                  <div className="filter-options">
                    <button
                      className={`filter-option ${!selectedFilters.developer ? 'active' : ''}`}
                      onClick={() => handleFilterChange('developer', '')}
                    >
                      {language === "ar" ? "الكل" : "All"}
                    </button>
                    {developers.map((dev) => (
                      <button
                        key={dev.id}
                        className={`filter-option ${selectedFilters.developer === dev.name ? 'active' : ''}`}
                        onClick={() => handleFilterChange('developer', dev.name)}
                      >
                        {dev.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Preferences Filter Dropdown */}
              {activeFilterTab === 'preferences' && (
                <div className="filter-dropdown-content">
                  {/* <div className="filter-title">
                    {language === "ar" ? "التفضيلات" : "Preferences"}
                  </div> */}
                  <div className="filter-options">
                    <button
                      className={`filter-option ${!selectedFilters.preferences ? 'active' : ''}`}
                      onClick={() => handleFilterChange('preferences', '')}
                    >
                      {language === "ar" ? "الكل" : "All"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.preferences === 'latest' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('preferences', 'latest')}
                    >
                      {language === "ar" ? "المضاف حديثاً" : "Latest Added"}
                    </button>
                    <button
                      className={`filter-option ${selectedFilters.preferences === 'lowest_price' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('preferences', 'lowest_price')}
                    >
                      {language === "ar" ? "أقل سعر" : "Lowest Price"}
                    </button>
                  </div>
                </div>
              )}

              {/* Delivery Date Filter Dropdown - Year Range */}
              {activeFilterTab === 'deliveryDate' && (
                <div className="filter-dropdown-content">
                  <div className="filter-title">
                    {language === "ar" ? "تاريخ التسليم" : "Delivery Date"}
                  </div>
                  <div className="filter-options">
                    <button
                      className={`filter-option ${!selectedFilters.deliveryDateFrom && !selectedFilters.deliveryDateTo ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedFilters(prev => ({
                          ...prev,
                          deliveryDateFrom: null,
                          deliveryDateTo: null
                        }));
                        applyFiltersToSearch();
                      }}
                    >
                      {language === "ar" ? "الكل" : "All"}
                    </button>
                    <div className="year-range-container">
                      <div className="year-range-label">
                        {language === "ar" ? "من السنة" : "From Year"}
                      </div>
                      <select
                        className="year-select"
                        value={selectedFilters.deliveryDateFrom || ''}
                        onChange={(e) => {
                          setSelectedFilters(prev => ({
                            ...prev,
                            deliveryDateFrom: e.target.value ? parseInt(e.target.value) : null
                          }));
                          applyFiltersToSearch();
                        }}
                      >
                        <option value="">{language === "ar" ? "اختر السنة" : "Select Year"}</option>
                        {Array.from({ length: 11 }, (_, i) => {
                          const year = new Date().getFullYear() + i;
                          return (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          );
                        })}
                      </select>

                      <div className="year-range-label">
                        {language === "ar" ? "إلى السنة" : "To Year"}
                      </div>
                      <select
                        className="year-select"
                        value={selectedFilters.deliveryDateTo || ''}
                        onChange={(e) => {
                          setSelectedFilters(prev => ({
                            ...prev,
                            deliveryDateTo: e.target.value ? parseInt(e.target.value) : null
                          }));
                          applyFiltersToSearch();
                        }}
                      >
                        <option value="">{language === "ar" ? "اختر السنة" : "Select Year"}</option>
                        {Array.from({ length: 11 }, (_, i) => {
                          const year = new Date().getFullYear() + i;
                          return (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

      {/* Recent Projects - Show on initial load, hide when user interacts with tabs or after map interaction */}
      {mode === 'project' && !isInitialLoading && recentProjects.length > 0 && !hideRecentProjectsPermanently && (isInitialLoad || !['project-search', 'project-status', 'project-city'].includes(activeTab)) && (
        <div className="recent-projects-bar">
          <div className="recent-projects-container">
            {/* <div className="recent-title">
              {language === "ar" ? "المشاريع الحديثة" : "Recent Projects"}
            </div> */}
            <div className="recent-projects-horizontal">
              {recentProjects.map(project => (
                <div
                  key={project.id}
                  className="recent-project-card"
                  onClick={() => handleProjectClick(project)}
                >
                  <div className="project-info">
                    <h5 className="project-name">
                      {language === 'ar' ? (project.name_ar || project.name) : (project.name_en || project.name)}
                    </h5>
                    <p className="project-details">
                      <span className="developer-name">{project.developer_name}</span>
                      {project.city && (
                        <>
                          <span className="separator"> • </span>
                          <span className="city">
                            {language === 'ar' ? project.city.name_ar : project.city.name_en}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  <div
                    className="project-status-indicator"
                    style={{ backgroundColor: project.status_color }}
                  ></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
